import * as THREE from 'three/webgpu';
import {
    SketchLineObject,
    SketchArcObject,
    SketchEllipseObject,
    SketchParabolaObject,
    SketchSplineObject,
    SketchStyleSplineObject,
    SketchPointObject,
} from '../../mesh/sketch';
import {
    isSketchLineObject,
    isSketchArcObject,
    isSketchEllipseObject,
    isSketchParabolaObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';
import {
    tangentDirectionOnCircleLocal,
    tangentDirectionOnEllipseLocal,
} from '../../manager/tangent/TangentLineComputer';

/**
 * 支持为切线弧提供起点切线方向的草图边类型。
 * 说明：
 * 1. 直线提供整体方向；圆弧/椭圆弧/抛物线/样条提供端点处沿曲线行进方向的切线；
 * 2. 圆是闭合曲线且没有端点协议，无法作为切线弧起点来源，故不在此列。
 */
export type TangentArcSourceEdge =
    | SketchLineObject
    | SketchArcObject
    | SketchEllipseObject
    | SketchParabolaObject
    | SketchSplineObject
    | SketchStyleSplineObject;

/**
 * 判断草图边是否为切线弧可支持的来源边。
 * @param value 待判断对象。
 * @returns 是否为可支持的来源边。
 */
export function isTangentArcSourceEdge(value: unknown): value is TangentArcSourceEdge {
    return isSketchLineObject(value)
        || isSketchArcObject(value)
        || isSketchEllipseObject(value)
        || isSketchParabolaObject(value)
        || isSketchSplineObject(value)
        || isSketchStyleSplineObject(value);
}

/**
 * 获取来源草图边的起点控制点。
 * @param edge 来源草图边。
 * @returns 起点控制点对象。
 */
export function getSourceEdgeStartPoint(edge: TangentArcSourceEdge): SketchPointObject {
    if (isSketchLineObject(edge)) {
        return edge.startPoint;
    }
    if (isSketchArcObject(edge)) {
        return edge.getStartPoint();
    }
    if (isSketchEllipseObject(edge)) {
        return edge.getStartPoint();
    }
    if (isSketchParabolaObject(edge)) {
        return edge.getStartPoint();
    }
    return edge.startPoint;
}

/**
 * 获取来源草图边的终点控制点。
 * @param edge 来源草图边。
 * @returns 终点控制点对象。
 */
export function getSourceEdgeEndPoint(edge: TangentArcSourceEdge): SketchPointObject {
    if (isSketchLineObject(edge)) {
        return edge.endPoint;
    }
    if (isSketchArcObject(edge)) {
        return edge.getEndPoint();
    }
    if (isSketchEllipseObject(edge)) {
        return edge.getEndPoint();
    }
    if (isSketchParabolaObject(edge)) {
        return edge.getEndPoint();
    }
    return edge.endPoint;
}

/**
 * 解析草图边在指定端点处沿曲线行进方向的单位切线（世界坐标）。
 *
 * 说明：
 * 1. 直线：行进方向恒为起点指向终点；
 * 2. 圆弧/椭圆弧：行进方向为扫掠方向（逆时针为半径方向逆时针转 90°，顺时针取反）；
 * 3. 抛物线：对参数方程 P(t) = apex + side·t + axis·(t²/4f) 求导得 dP/dt = side + axis·(t/2f)；
 * 4. 样条/样式样条：使用采样点列端点差分近似切线；
 * 5. 切线弧从端点起步时，延续方向 = 行进方向 ×（选中终点取 +1，选中起点取 −1），
 *    由调用方按端点角色决定正负，本函数只返回行进方向本身。
 *
 * @param edge 来源草图边。
 * @param endpoint 曲线上的端点（必须为该边的起点或终点控制点）。
 * @returns 归一化行进切线方向；边类型不支持或端点不匹配时返回 null。
 */
export function resolveEdgeTravelTangentAtEndpoint(
    edge: TangentArcSourceEdge,
    endpoint: SketchPointObject,
): THREE.Vector3 | null {
    if (isSketchLineObject(edge)) {
        return resolveLineTravelTangent(edge);
    }
    if (isSketchArcObject(edge)) {
        return resolveArcTravelTangent(edge, endpoint);
    }
    if (isSketchEllipseObject(edge)) {
        return resolveEllipseTravelTangent(edge, endpoint);
    }
    if (isSketchParabolaObject(edge)) {
        return resolveParabolaTravelTangent(edge, endpoint);
    }
    if (isSketchSplineObject(edge) || isSketchStyleSplineObject(edge)) {
        return resolveSplineTravelTangent(edge, endpoint);
    }
    return null;
}

/**
 * 解析直线行进切线（起点指向终点）。
 * @param line 来源直线。
 * @returns 归一化方向；退化直线返回 null。
 */
function resolveLineTravelTangent(line: SketchLineObject): THREE.Vector3 | null {
    return line.endPoint.getPosition()
        .clone()
        .sub(line.startPoint.getPosition())
        .normalize();
}

/**
 * 解析圆弧在指定端点处的行进切线。
 * @param arc 来源圆弧。
 * @param endpoint 端点。
 * @returns 归一化行进切线；端点不匹配返回 null。
 */
function resolveArcTravelTangent(arc: SketchArcObject, endpoint: SketchPointObject): THREE.Vector3 | null {
    const isStartPoint = arc.getStartPoint() === endpoint;
    const isEndPoint = arc.getEndPoint() === endpoint;
    if (!isStartPoint && !isEndPoint) {
        return null;
    }

    const plane = arc.referencePlane;
    const definition = arc.getArcDefinition();
    const centerLocal = plane.worldToLocal(definition.centerWorld);
    const pointLocal = plane.worldToLocal(endpoint.getPosition());
    // tangentDirectionOnCircleLocal 固定返回逆时针方向，顺时针扫掠时取反。
    const travelLocal = tangentDirectionOnCircleLocal(pointLocal, centerLocal);
    if (definition.clockwise) {
        travelLocal.multiplyScalar(-1);
    }
    return toWorldDirection(plane, travelLocal);
}

/**
 * 解析椭圆弧在指定端点处的行进切线。
 * @param ellipse 来源椭圆弧。
 * @param endpoint 端点。
 * @returns 归一化行进切线；端点不匹配返回 null。
 */
function resolveEllipseTravelTangent(ellipse: SketchEllipseObject, endpoint: SketchPointObject): THREE.Vector3 | null {
    const isStartPoint = ellipse.getStartPoint() === endpoint;
    const isEndPoint = ellipse.getEndPoint() === endpoint;
    if (!isStartPoint && !isEndPoint) {
        return null;
    }

    const plane = ellipse.referencePlane;
    const definition = ellipse.getEllipseDefinition();
    const centerLocal = plane.worldToLocal(definition.centerWorld);
    const pointLocal = plane.worldToLocal(endpoint.getPosition());
    // tangentDirectionOnEllipseLocal 固定返回参数增大（逆时针）方向，顺时针采样时取反。
    const travelLocal = tangentDirectionOnEllipseLocal(
        pointLocal,
        centerLocal,
        definition.radiusX,
        definition.radiusY,
        definition.rotation,
    );
    if (definition.clockwise) {
        travelLocal.multiplyScalar(-1);
    }
    return toWorldDirection(plane, travelLocal);
}

/**
 * 解析抛物线在指定端点处的行进切线（参数增大方向）。
 * @param parabola 来源抛物线。
 * @param endpoint 端点。
 * @returns 归一化行进切线；端点不匹配或定义退化返回 null。
 */
function resolveParabolaTravelTangent(parabola: SketchParabolaObject, endpoint: SketchPointObject): THREE.Vector3 | null {
    const definition = parabola.getParabolaDefinition();
    const isStartPoint = parabola.getStartPoint() === endpoint;
    const isEndPoint = parabola.getEndPoint() === endpoint;
    if (!isStartPoint && !isEndPoint) {
        return null;
    }

    const plane = parabola.referencePlane;
    const normal = plane.normal.clone().normalize();
    const axisVector = definition.focalWorld.clone().sub(definition.apexWorld);
    axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
    const focalLength = axisVector.length();
    if (focalLength < 1e-9) {
        return null;
    }
    const axisDir = axisVector.normalize();
    const sideDir = new THREE.Vector3().crossVectors(axisDir, normal).normalize();

    // 端点参数：起点取 startParameter、终点取 endParameter。
    const parameter = isEndPoint ? definition.endParameter : definition.startParameter;
    // 对参数方程求导：dP/dt = sideDir + axisDir * (t / (2f))。
    const travel = sideDir
        .clone()
        .addScaledVector(axisDir, parameter / (2 * focalLength));
    if (travel.lengthSq() < 1e-12) {
        return null;
    }
    return travel.normalize();
}

/**
 * 解析样条/样式样条在指定端点处的行进切线（采样点列端点差分）。
 * @param spline 来源样条。
 * @param endpoint 端点。
 * @returns 归一化行进切线；采样点不足或端点不匹配返回 null。
 */
function resolveSplineTravelTangent(
    spline: SketchSplineObject | SketchStyleSplineObject,
    endpoint: SketchPointObject,
): THREE.Vector3 | null {
    const isStartPoint = spline.startPoint === endpoint;
    const isEndPoint = spline.endPoint === endpoint;
    if (!isStartPoint && !isEndPoint) {
        return null;
    }

    const curvePoints = spline.getCurvePoints();
    if (curvePoints.length < 2) {
        return null;
    }

    // 起点用前两个采样点差分，终点用后两个采样点差分，保持与采样行进方向一致。
    const travel = isEndPoint
        ? curvePoints[curvePoints.length - 1]!.clone().sub(curvePoints[curvePoints.length - 2]!)
        : curvePoints[1]!.clone().sub(curvePoints[0]!);
    if (travel.lengthSq() < 1e-12) {
        return null;
    }
    return travel.normalize();
}

/**
 * 将参考面局部二维方向转换为世界三维方向。
 * @param plane 参考面。
 * @param localDirection 局部二维方向（无需归一化）。
 * @returns 归一化世界方向。
 */
function toWorldDirection(
    plane: {xAxis: THREE.Vector3; yAxis: THREE.Vector3},
    localDirection: THREE.Vector2,
): THREE.Vector3 {
    return new THREE.Vector3()
        .addScaledVector(plane.xAxis, localDirection.x)
        .addScaledVector(plane.yAxis, localDirection.y)
        .normalize();
}
